import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Textarea } from '@/components/ui/textarea'
import { CheckCheck, Check, Clock } from 'lucide-react'
import { forwardRef, memo } from 'react'
import { formatMessageWithLinks } from '@/utils/linkUtils'
import { getCharacterTypeSigil } from '@/utils/characterUtils'
import { MessageMedia } from './MessageMedia'
import { MessageContextMenu } from './MessageContextMenu'
import { DiceRollDisplay } from './DiceRollDisplay'
import { PollResultMessage } from '@/modules/poll'
import type { ChatMode } from '@/types/message'
// ProcessedMessage tem uma unica definicao (MessageList.types) — antes estava
// duplicada aqui e no arquivo de tipos, com risco de divergencia.
import type { ProcessedMessage } from './MessageList/MessageList.types'

interface MessageBubbleProps {
  message: ProcessedMessage
  chatMode: ChatMode
  isMaster: boolean
  isEditing: boolean
  editingContent: string
  onEdit: (id: string, content: string) => void
  onDelete: (id: string) => void
  onEditChange: (content: string) => void
  onSaveEdit: (id: string) => void
  onCancelEdit: () => void
}

// Comparação custom: re-renderiza só quando algo realmente afeta este bubble.
// Sem isso, cada keystroke do input causa re-render de 100 bubbles (markdown
// + KaTeX = travamento).
//
// Comparamos os CAMPOS de message em vez da referência: o processedMessages
// (MessageListNative) recria todos os objetos a cada mensagem nova, então
// `prev.message !== next.message` era sempre true e o memo não protegia o
// caso mais quente (chegada de mensagem re-renderizava os 100 bubbles).
const messageFieldsEqual = (a: MessageBubbleProps['message'], b: MessageBubbleProps['message']) => {
  return (
    a.id === b.id &&
    a.isOwnMessage === b.isOwnMessage &&
    a.showAvatar === b.showAvatar &&
    a.avatarUrl === b.avatarUrl &&
    a.isFirstInGroup === b.isFirstInGroup &&
    a.isLastInGroup === b.isLastInGroup &&
    a.displayContent === b.displayContent &&
    a.displayTimestamp === b.displayTimestamp &&
    a.displayUserName === b.displayUserName &&
    a.characterType === b.characterType &&
    a.messageType === b.messageType &&
    a.editedAt === b.editedAt &&
    a.is_read === b.is_read &&
    a.diceType === b.diceType &&
    a.diceResult === b.diceResult &&
    a.dicePool === b.dicePool &&
    a.mediaFile === b.mediaFile
  )
}

const arePropsEqual = (prev: MessageBubbleProps, next: MessageBubbleProps) => {
  if (!messageFieldsEqual(prev.message, next.message)) return false
  if (prev.chatMode !== next.chatMode) return false
  if (prev.isMaster !== next.isMaster) return false
  if (prev.isEditing !== next.isEditing) return false
  // editingContent só importa pra bubble que está sendo editada
  if (prev.isEditing && prev.editingContent !== next.editingContent) return false
  if (prev.onEdit !== next.onEdit) return false
  if (prev.onDelete !== next.onDelete) return false
  if (prev.onEditChange !== next.onEditChange) return false
  if (prev.onSaveEdit !== next.onSaveEdit) return false
  if (prev.onCancelEdit !== next.onCancelEdit) return false
  return true
}

const MessageBubbleInner = forwardRef<HTMLTextAreaElement, MessageBubbleProps>(function MessageBubble({
  message,
  chatMode,
  isMaster,
  isEditing,
  editingContent,
  onEdit,
  onDelete,
  onEditChange,
  onSaveEdit,
  onCancelEdit
}, editInputRef) {
  // Detectar se é uma mensagem pendente (otimista)
  const isPending = message.id.startsWith('temp-')

  /**
   * A jogada de dados NÃO se condensa (decisão 4 do Paulo, 2026-07-27).
   *
   * Todo o resto desta bolha encolheu para caber mais fala na tela; a jogada
   * anda no sentido contrário de propósito. Ela é o momento em que a mesa
   * inteira olha junto — cada dado, a conta, o veredito — e o que mostra é
   * conteúdo de aula, não recado. Espremê-la economizaria pixels no lugar
   * exato onde eles estão sendo bem gastos.
   */
  const éJogada =
    message.messageType === 'dice-roll' ||
    message.messageType === 'dice_roll' ||
    message.messageType === 'poll_result'

  /**
   * A identidade só aparece na PRIMEIRA fala de uma sequência, e nunca na
   * própria: alinhamento e cor já dizem quem falou. É o mesmo que o WhatsApp
   * faz num grupo, e pela mesma razão — repetir o nome a cada linha é gastar
   * ~26px para dizer o que a linha de cima já disse.
   */
  const mostraIdentidade =
    chatMode === 'group' && message.isFirstInGroup && !message.isOwnMessage

  const sigla = message.characterType ? getCharacterTypeSigil(message.characterType) : null

  /**
   * O avatar do personagem, como num grupo do WhatsApp: fora da bolha, à
   * esquerda, só na primeira fala de uma sequência, nenhum na própria fala.
   *
   * A foto já existia de ponta a ponta (coluna `characters.image_url`, upload,
   * tela de edição) — só não chegava ao chat. Numa mesa de RPG ela vale mais do
   * que num grupo comum: o personagem é a identidade do jogo, e reconhecer o
   * rosto é mais rápido do que ler o nome numa tela cheia de falas.
   *
   * **30px de propósito.** A bolha de uma linha tem ~36px; um avatar maior
   * empurraria a altura para cima e desfaria metade da fase 3 — o oposto do que
   * este plano inteiro está fazendo.
   */
  const mostraAvatar = chatMode === 'group' && message.showAvatar
  // Espaço reservado nas falas seguintes: sem ele a segunda bolha da sequência
  // encostaria na margem e a coluna ficaria serrilhada.
  const reservaEspacoDoAvatar = chatMode === 'group' && !message.isOwnMessage
  const iniciais = message.displayUserName
    .split(/\s+/)
    .slice(0, 2)
    .map(parte => parte.charAt(0).toUpperCase())
    .join('')

  return (
    <div
      className={cn(
        "flex mb-0.5 px-2 md:px-4 items-end gap-1.5",
        message.isOwnMessage ? "justify-end" : "justify-start",
        message.isLastInGroup ? (éJogada ? "mb-3" : "mb-2") : "",
        isPending && "opacity-80"
      )}
    >
      {reservaEspacoDoAvatar && (
        <div
          className={cn('relative flex-shrink-0 self-start mt-0.5', !mostraAvatar && 'invisible')}
          aria-hidden={!mostraAvatar}
        >
          <Avatar className="size-[30px] border border-game-border-primary dark:border-zinc-700">
            {message.avatarUrl && (
              <AvatarImage src={message.avatarUrl} alt={message.displayUserName} />
            )}
            <AvatarFallback className="text-[11px] font-semibold bg-game-tertiary dark:bg-zinc-800 text-game-secondary">
              {iniciais}
            </AvatarFallback>
          </Avatar>
          {/* A sigla do papel migra para o canto do avatar. Se ficasse tambem
              na linha do texto, o nome seria empurrado para linha propria e os
              ~26px que a fase 3 economizou voltariam pela porta dos fundos.

              Na JOGADA ela nao aparece: la a identidade tem linha propria e o
              cracha inteiro ("Jogador", com icone), e duas marcas do mesmo
              papel na mesma bolha e uma a mais. */}
          {sigla && mostraAvatar && !éJogada && (
            <span
              title={sigla.titulo}
              aria-label={sigla.titulo}
              className={cn(
                'absolute -bottom-0.5 -right-0.5 inline-flex items-center justify-center',
                'w-3.5 h-3.5 rounded-full border text-[9px] font-bold leading-none',
                sigla.classe
              )}
            >
              {sigla.letra}
            </span>
          )}
        </div>
      )}
      <div
        className={cn(
          "rounded-2xl px-4 max-w-[85%] md:max-w-[75%] relative shadow-lg group transition-all duration-200",
          éJogada ? "py-3" : "py-2",
          chatMode === 'private' ? (
            message.isOwnMessage
              ? "bg-gradient-to-br from-violet-600/90 to-violet-700/90 text-white border border-violet-500/30 rounded-br-md shadow-violet-500/20"
              : "bg-game-tertiary dark:bg-zinc-800/80 backdrop-blur-sm text-game-primary dark:text-zinc-100 border border-game-border-primary dark:border-zinc-700/50 rounded-bl-md"
          ) : (
            message.isOwnMessage
              ? "bg-gradient-to-br from-violet-600/90 to-violet-700/90 text-white border border-violet-500/30 rounded-br-md shadow-violet-500/20"
              : "bg-game-tertiary dark:bg-zinc-800/80 backdrop-blur-sm text-game-primary dark:text-zinc-100 border border-game-border-primary dark:border-zinc-700/50 rounded-bl-md"
          )
        )}
      >
        {/* Menu de contexto (apenas para mestre no chat principal) */}
        {chatMode === 'group' && isMaster && (
          <div className="absolute top-2 right-2 z-10">
            <MessageContextMenu
              messageId={message.id}
              onEdit={() => onEdit(message.id, message.displayContent)}
              onDelete={() => onDelete(message.id)}
              canEdit={message.messageType !== 'dice-roll' && message.messageType !== 'dice_roll' && message.messageType !== 'poll_result'}
            />
          </div>
        )}

        {/* A identidade da jogada fica em linha propria, com o cracha inteiro:
            e o unico lugar onde a bolha nao se condensa (ver `éJogada`). */}
        {mostraIdentidade && éJogada && (
          <div className="flex items-center gap-2 mb-1.5">
            <span className="text-sm font-semibold text-violet-400">
              {message.displayUserName}
            </span>
            {message.typeStyle && (
              <Badge
                variant="outline"
                className={`${message.typeStyle.badge} text-xs px-2 py-0 h-5 flex items-center gap-1`}
              >
                {(() => {
                  const TypeIcon = message.typeStyle.icon
                  return <TypeIcon className={`h-3 w-3 ${message.typeStyle.iconColor}`} />
                })()}
                {message.typeStyle.label}
              </Badge>
            )}
          </div>
        )}

        {/* Foto ou video sem legenda nao tem linha de texto onde encaixar a
            identidade — ela ganha a sua, condensada (sigla + nome), so aqui. */}
        {mostraIdentidade && !éJogada && !message.displayContent && message.mediaFile && (
          <div className="mb-1 text-sm font-semibold text-violet-400">
            {message.displayUserName}
          </div>
        )}

        {/* Midia da mensagem */}
        {message.mediaFile && (
          <div className="mb-2">
            <MessageMedia
              mediaFile={message.mediaFile}
              isOwnMessage={message.isOwnMessage}
            />
          </div>
        )}

        {/* Conteudo da mensagem - edicao inline ou visualizacao */}
        {isEditing ? (
          <div className="space-y-2">
            <Textarea
              ref={editInputRef}
              value={editingContent}
              onChange={(e) => onEditChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                  e.preventDefault()
                  onSaveEdit(message.id)
                } else if (e.key === 'Escape') {
                  e.preventDefault()
                  onCancelEdit()
                }
              }}
              className="w-full text-sm resize-none min-h-[60px] bg-game-secondary dark:bg-zinc-900/50 border-game-border-primary dark:border-zinc-700/50 text-game-primary dark:text-zinc-100 rounded-lg focus:border-violet-500/50 focus:ring-violet-500/20"
              rows={Math.min(editingContent.split('\n').length, 6)}
            />
            <div className="flex justify-end gap-2">
              <Button
                size="sm"
                variant="ghost"
                onClick={onCancelEdit}
                className="h-7 text-xs text-game-muted hover:text-game-primary dark:hover:text-zinc-200 hover:bg-game-hover dark:hover:bg-zinc-700/50"
              >
                Cancelar
              </Button>
              <Button
                size="sm"
                onClick={() => onSaveEdit(message.id)}
                className="h-7 text-xs bg-violet-600 hover:bg-violet-500 text-white"
              >
                Salvar
              </Button>
            </div>
          </div>
        ) : message.messageType === 'poll_result' ? (
          <PollResultMessage
            content={message.displayContent}
            isOwnMessage={message.isOwnMessage}
          />
        ) : (message.messageType === 'dice-roll' || message.messageType === 'dice_roll')
            && message.diceType
            && typeof message.diceResult === 'number' ? (
          <DiceRollDisplay
            diceType={message.diceType}
            result={message.diceResult}
            isOwnMessage={message.isOwnMessage}
            test={message.diceTest}
            pool={message.dicePool}
          />
        ) : message.displayContent ? (
          // A identidade entra NA LINHA DO TEXTO, nao acima dela: e o corte que
          // derruba a bolha de ~100px para ~58px. `leading-5` (20px) no lugar
          // do `leading-relaxed` (~22,75px) — 14px de fonte fica, porque abaixo
          // disso quem le de longe numa sala comeca a perder.
          <p className="text-sm leading-5 whitespace-pre-wrap break-words pr-12">
            {mostraIdentidade && (
              // So o nome: a sigla do papel vive no canto do avatar (fase 5).
              <span className="font-semibold text-violet-400 mr-1.5">
                {message.displayUserName}
              </span>
            )}
            {formatMessageWithLinks(message.displayContent)}
          </p>
        ) : message.mediaFile ? (
          null
        ) : null}

        {/* A hora encaixa no canto da bolha em vez de ocupar linha propria — o
            `pr-12` do texto acima reserva o lugar dela. Custo de altura: zero.
            Na jogada ela continua embaixo: la a conta tem largura variavel e a
            hora sobreposta cairia em cima do veredito. */}
        <div className={cn(
          "text-[11px] flex justify-end items-center gap-1.5",
          éJogada ? "mt-2" : "absolute bottom-1.5 right-3",
          message.isOwnMessage ? "text-violet-200/70" : "text-game-muted"
        )}>
          {new Date(message.displayTimestamp).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit'
          })}

          {/* Indicador de mensagem editada */}
          {chatMode === 'group' && message.editedAt && (
            <span className="text-game-muted italic ml-1">(editado)</span>
          )}

          {/* Indicadores de status da mensagem */}
          {message.isOwnMessage && (
            <div className="ml-1">
              {message.id.startsWith('temp-') ? (
                // Mensagem otimista pendente de confirmação
                <span title="Enviando"><Clock className="h-3.5 w-3.5 text-violet-200/50 animate-pulse" /></span>
              ) : chatMode === 'private' ? (
                // Chat privado: mostrar indicadores de leitura baseados no campo is_read
                message.is_read ? (
                  <span title="Lida"><CheckCheck className="h-4 w-4 text-emerald-400" /></span>
                ) : (
                  <span title="Entregue"><Check className="h-4 w-4 text-violet-200/70" /></span>
                )
              ) : (
                // Chat em grupo: indicador padrao
                <span title="Enviado"><CheckCheck className="h-4 w-4 text-emerald-400" /></span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
})

export const MessageBubble = memo(MessageBubbleInner, arePropsEqual)
